import React from 'react';
import { Link } from 'react-router-dom';
import { FaHome, FaChevronRight, FaWifi, FaSignal, FaChartLine, FaCheckCircle, FaArrowRight, FaPhone, FaCogs, FaMapMarkerAlt, FaClipboardCheck } from 'react-icons/fa';
import Navigation from '../components/Navigation';
import TopBar from '../components/TopBar';
import Footer from '../components/Footer';

const WAPDeploymentPage = () => {
    const services = [
        {
            icon: <FaWifi size={24} />,
            title: 'Enterprise WiFi Installation',
            description: 'Deploy high-performance wireless access points for seamless connectivity.',
            gradient: 'from-green-500 to-emerald-500'
        },
        {
            icon: <FaMapMarkerAlt size={24} />,
            title: 'Site Surveys',
            description: 'Comprehensive RF site surveys to optimize access point placement.',
            gradient: 'from-blue-600 to-cyan-600'
        },
        {
            icon: <FaSignal size={24} />,
            title: 'Coverage Optimization',
            description: 'Ensure complete coverage with no dead zones across your facilities.',
            gradient: 'from-purple-600 to-indigo-600'
        },
        {
            icon: <FaChartLine size={24} />,
            title: 'Performance Tuning',
            description: 'Optimize channel allocation and power settings for peak performance.',
            gradient: 'from-orange-500 to-red-500'
        },
        {
            icon: <FaCogs size={24} />,
            title: 'Controller Configuration',
            description: 'Set up and configure wireless controllers for centralized management.',
            gradient: 'from-teal-500 to-cyan-500'
        },
        {
            icon: <FaClipboardCheck size={24} />,
            title: 'Validation Testing',
            description: 'Thorough testing to verify coverage, speed, and reliability.',
            gradient: 'from-pink-500 to-rose-500'
        }
    ];

    const benefits = [
        'Complete wireless coverage',
        'High-density support',
        'Seamless roaming',
        'Guest network setup',
        'Security compliance',
        'Ongoing support'
    ];

    return (
        <div className="font-sans">
            <TopBar />
            <Navigation />

            {/* Hero Section */}
            <section className="relative bg-gradient-to-br from-gray-900 via-green-900 to-emerald-900 py-24 overflow-hidden">
                <div className="absolute inset-0 opacity-10">
                    <div className="absolute inset-0" style={{
                        backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
                    }}></div>
                </div>

                <div className="absolute top-0 right-0 w-96 h-96 bg-green-500 rounded-full filter blur-3xl opacity-20 translate-x-1/2 -translate-y-1/2"></div>
                <div className="absolute bottom-0 left-0 w-96 h-96 bg-emerald-500 rounded-full filter blur-3xl opacity-20 -translate-x-1/2 translate-y-1/2"></div>

                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
                    <nav className="flex items-center gap-2 text-sm text-gray-300 mb-8">
                        <Link to="/" className="flex items-center gap-1 hover:text-white transition-colors">
                            <FaHome size={14} />
                            <span>Home</span>
                        </Link>
                        <FaChevronRight size={10} className="text-gray-500" />
                        <Link to="/services/national-rollouts" className="hover:text-white transition-colors">National Rollouts</Link>
                        <FaChevronRight size={10} className="text-gray-500" />
                        <span className="text-green-400">WAP Deployment</span>
                    </nav>

                    <div className="grid lg:grid-cols-2 gap-12 items-center">
                        <div>
                            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
                                <span className="text-transparent bg-clip-text bg-gradient-to-r from-green-400 to-emerald-400">WAP</span> Deployment
                            </h1>
                            <p className="text-xl text-gray-300 mb-8 leading-relaxed">
                                Enterprise wireless access point deployment with site surveys, professional installation, and optimization for complete coverage.
                            </p>
                            <div className="flex flex-wrap gap-4">
                                <Link to="/contact" className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-xl font-semibold hover:shadow-lg hover:shadow-green-500/30 transition-all duration-300">
                                    Get Quote
                                    <FaArrowRight size={14} />
                                </Link>
                                <a href="tel:+12014099845" className="inline-flex items-center gap-2 px-8 py-4 border-2 border-white/30 text-white rounded-xl font-semibold hover:bg-white/10 transition-all duration-300">
                                    <FaPhone size={14} />
                                    Call Us Now
                                </a>
                            </div>
                        </div>
                        <div className="relative">
                            <div className="bg-gradient-to-br from-green-600/20 to-emerald-600/20 backdrop-blur-sm rounded-2xl p-8 border border-white/10">
                                <img
                                    src="https://images.unsplash.com/photo-1516044734145-07ca8eef8731?w=600&h=400&fit=crop"
                                    alt="WAP Deployment"
                                    className="w-full h-80 object-cover rounded-xl shadow-2xl"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Services Section */}
            <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
                <div className="max-w-7xl mx-auto">
                    <div className="text-center mb-16">
                        <span className="inline-block px-4 py-2 bg-green-100 text-green-700 rounded-full text-sm font-semibold mb-4">Our Services</span>
                        <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Complete WAP Deployment Solutions</h2>
                        <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                            From site survey to installation and optimization, we handle every aspect of your wireless deployment
                        </p>
                    </div>

                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                        {services.map((service, index) => (
                            <div
                                key={index}
                                className="group bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-100 hover:border-green-200"
                            >
                                <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${service.gradient} flex items-center justify-center text-white mb-6 group-hover:scale-110 transition-transform duration-300`}>
                                    {service.icon}
                                </div>
                                <h3 className="text-xl font-bold text-gray-900 mb-3">{service.title}</h3>
                                <p className="text-gray-600 leading-relaxed">{service.description}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Benefits Section */}
            <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-gray-50 to-green-50">
                <div className="max-w-7xl mx-auto">
                    <div className="grid lg:grid-cols-2 gap-16 items-center">
                        <div>
                            <img
                                src="https://images.unsplash.com/photo-1497366216548-37526070297c?w=600&h=500&fit=crop"
                                alt="Modern Office"
                                className="w-full h-auto rounded-2xl shadow-2xl"
                            />
                        </div>
                        <div>
                            <span className="inline-block px-4 py-2 bg-green-100 text-green-700 rounded-full text-sm font-semibold mb-4">Benefits</span>
                            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">Why Professional WAP Deployment?</h2>
                            <p className="text-lg text-gray-600 mb-8">
                                A properly deployed wireless network is essential for modern business operations.
                            </p>
                            <div className="grid grid-cols-2 gap-4">
                                {benefits.map((benefit, index) => (
                                    <div key={index} className="flex items-center gap-3">
                                        <FaCheckCircle className="text-green-500 flex-shrink-0" size={20} />
                                        <span className="text-gray-700 font-medium">{benefit}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-green-600 to-emerald-600">
                <div className="max-w-4xl mx-auto text-center">
                    <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">Ready to Go Wireless?</h2>
                    <p className="text-xl text-green-100 mb-8">
                        Get a free site survey and customized wireless deployment plan.
                    </p>
                    <div className="flex flex-wrap justify-center gap-4">
                        <Link to="/contact" className="inline-flex items-center gap-2 px-8 py-4 bg-white text-green-600 rounded-xl font-semibold hover:shadow-lg transition-all duration-300">
                            Request Site Survey
                            <FaArrowRight size={14} />
                        </Link>
                        <a href="tel:+12014099845" className="inline-flex items-center gap-2 px-8 py-4 border-2 border-white text-white rounded-xl font-semibold hover:bg-white/10 transition-all duration-300">
                            <FaPhone size={14} />
                            Call Now
                        </a>
                    </div>
                </div>
            </section>

            <Footer />
        </div>
    );
};

export default WAPDeploymentPage;
